import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import { Eye, EyeOff, ArrowUpRight, ArrowLeft, Building2, HardHat, Check, Loader2, Info, MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtError } from "@/lib/api";
import { AuthShell, CARD } from "@/components/landing/AuthShell";


/* --- Écran 1 : choix du rôle ---------------------------------------- */

function RoleCard({ testId, Icon, accent, label, promise, onPick }) {
  return (
    <button
      type="button"
      onClick={onPick}
      className="group relative w-full text-left bg-white border border-[#1B3A5C]/10 rounded-[8px] p-5 md:p-7 hover:border-[color:var(--role-accent)] hover:-translate-y-0.5 transition-all duration-300"
      style={{ "--role-accent": accent }}
      data-testid={testId}
    >
      <div
        className="w-11 h-11 rounded-full border flex items-center justify-center mb-4"
        style={{ borderColor: accent, color: accent }}
      >
        <Icon size={22} strokeWidth={2} />
      </div>
      <span
        className="text-[11px] uppercase tracking-[0.22em] font-bold"
        style={{ color: accent }}
      >
        {label}
      </span>
      <h2 className="mt-2 font-display text-lg md:text-xl font-semibold text-[#0F2338] tracking-tight leading-snug">
        {promise}
      </h2>
      <div className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#1F2933] group-hover:text-[color:var(--role-accent)] transition-colors">
        Continuer <ArrowUpRight size={16} />
      </div>
    </button>
  );
}

function RoleStep({ onPick }) {
  return (
    <div>
      <div className="mb-6 md:mb-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-px w-8 bg-[#E8710A]" aria-hidden />
          <span className="text-[11px] uppercase tracking-[0.24em] font-bold text-[#1B3A5C] font-mono-l">
            Étape 1 / 2
          </span>
        </div>
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-[#0F2338] tracking-[-0.02em] leading-[1.1]">
          Vous êtes…
        </h1>
        <p className="mt-3 text-sm md:text-base text-[#3E4C59]">
          30 secondes. SIRET requis.
        </p>
      </div>

      <div className="grid gap-3 md:gap-4">
        <RoleCard
          testId="register-role-entreprise"
          Icon={Building2}
          accent="#1B3A5C"
          label="Donneur d'ordre"
          promise="Je cherche des sous-traitants."
          onPick={() => onPick("entreprise")}
        />
        <RoleCard
          testId="register-role-prestataire"
          Icon={HardHat}
          accent="#E8710A"
          label="Artisan, auto-entrepreneur ou TPE"
          promise="Je cherche des chantiers."
          onPick={() => onPick("prestataire")}
        />
      </div>
    </div>
  );
}

/* --- Écran 2 : formulaire ------------------------------------------- */

const TYPE_STRUCTURE_OPTIONS = [
  { v: "entreprise_generale", t: "Entreprise générale du bâtiment" },
  { v: "syndic", t: "Syndic de copropriété" },
  { v: "gestionnaire_patrimoine", t: "Gestionnaire de patrimoine" },
  { v: "agence_immo", t: "Agence immobilière" },
  { v: "expert_assurance", t: "Expert en assurance" },
  { v: "assureur_courtier", t: "Compagnie ou courtier en assurance" },
  { v: "maitre_oeuvre", t: "Maître d'œuvre" },
  { v: "autre", t: "Autre" },
];

function FormStep({ role, onBack, onSuccess }) {
  const { register } = useAuth();
  const [form, setForm] = useState({
    prenom: "", nom: "",
    raison_sociale: "", type_structure: "",
    siret: "", email: "", password: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  // SIRET auto-lookup — debounced 500 ms lorsqu'on atteint 14 chiffres
  const [siretStatus, setSiretStatus] = useState({ state: "idle" });
  // Saisie libre de la raison sociale uniquement si l'annuaire ne répond pas
  // (SIRET introuvable / API indisponible) ou si l'artisan corrige la dénomination.
  const [raisonManuelle, setRaisonManuelle] = useState(false);
  const raisonLibre = role === "entreprise" || raisonManuelle || siretStatus.state === "not_found";
  const lookupTimer = useRef(null);

  useEffect(() => {
    const digits = form.siret.replace(/\s/g, "");
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    if (digits.length !== 14) {
      setSiretStatus({ state: "idle" });
      return;
    }
    setSiretStatus({ state: "loading" });
    lookupTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/public/siret-lookup", { params: { siret: digits } });
        if (data.found) {
          setSiretStatus({ state: "found", data });
          // Dénomination officielle (recherche-entreprises.api.gouv.fr) → source de vérité
          setForm((f) => ({ ...f, raison_sociale: data.denomination || "" }));
          setRaisonManuelle(false);
        } else {
          setSiretStatus({ state: "not_found", reason: data.reason });
        }
      } catch {
        setSiretStatus({ state: "not_found", reason: "api_error" });
      }
    }, 500);
    return () => { if (lookupTimer.current) clearTimeout(lookupTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.siret]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Mot de passe : 8 caractères minimum.");
    const siretDigits = form.siret.replace(/\s/g, "");
    if (!/^\d{14}$/.test(siretDigits)) return toast.error("SIRET : 14 chiffres.");
    if (role === "entreprise") {
      if (!form.raison_sociale.trim()) return toast.error("Raison sociale obligatoire.");
      if (!form.type_structure) return toast.error("Sélectionnez un type de structure.");
    } else {
      if (!form.prenom.trim() || !form.nom.trim())
        return toast.error("Prénom et nom obligatoires.");
    }
    setBusy(true);
    try {
      const payload = {
        role,
        email: form.email,
        password: form.password,
        siret: siretDigits,
        raison_sociale: form.raison_sociale.trim() || null,
      };
      if (role === "entreprise") {
        payload.type_structure = form.type_structure;
      } else {
        payload.prenom = form.prenom.trim();
        payload.nom = form.nom.trim();
      }
      const u = await register(payload);
      onSuccess(u);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "siret_already_registered") {
        toast.error(detail.message, {
          duration: 8000,
          action: {
            label: "Se connecter",
            onClick: () => window.location.assign(detail.login_path || "/connexion"),
          },
        });
        return;
      }
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  const roleAccent = role === "entreprise" ? "#1B3A5C" : "#E8710A";

  return (
    <div>
      <div className="mb-6 md:mb-8">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-bold text-[#3E4C59] hover:text-[#1B3A5C] transition-colors mb-6"
          data-testid="register-back-btn"
        >
          <ArrowLeft size={14} /> Changer de profil
        </button>
        <div className="flex items-center gap-3 mb-6">
          <div className="h-px w-8" style={{ backgroundColor: roleAccent }} aria-hidden />
          <span
            className="text-[11px] uppercase tracking-[0.24em] font-bold font-mono-l"
            style={{ color: roleAccent }}
          >
            {role === "entreprise" ? "Donneur d'ordre" : "Prestataire"} — Étape 2 / 2
          </span>
        </div>
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-[#0F2338] tracking-[-0.02em] leading-[1.1]">
          Créez votre compte.
        </h1>
      </div>

      <form
        onSubmit={submit}
        className={CARD}
        data-testid="register-form"
      >
        {role === "prestataire" ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="register-prenom" className="label">Prénom</label>
              <input
                id="register-prenom"
                required
                autoComplete="given-name"
                className="input"
                value={form.prenom}
                onChange={set("prenom")}
                data-testid="register-prenom"
              />
            </div>
            <div>
              <label htmlFor="register-nom" className="label">Nom</label>
              <input
                id="register-nom"
                required
                autoComplete="family-name"
                className="input"
                value={form.nom}
                onChange={set("nom")}
                data-testid="register-nom"
              />
            </div>
          </div>
        ) : null}

        <div>
          <label htmlFor="register-siret" className="label">SIRET</label>
          <input
            id="register-siret"
            required
            inputMode="numeric"
            pattern="\d{14}"
            placeholder="14 chiffres"
            className="input"
            value={form.siret}
            onChange={set("siret")}
            data-testid="register-siret"
            autoComplete="off"
          />
          <SiretStatus status={siretStatus} onEdit={role === "prestataire" && !raisonManuelle ? () => setRaisonManuelle(true) : null} />
        </div>

        {raisonLibre && (
        <div>
          <label htmlFor="register-raison" className="label">
            Raison sociale
            {role === "prestataire" && (
              <span className="ml-1.5 font-normal text-[#3E4C59]">
                (SARL, EURL, micro-entrepreneur…)
              </span>
            )}
          </label>
          <input
            id="register-raison"
            required={role === "entreprise"}
            className="input"
            value={form.raison_sociale}
            onChange={set("raison_sociale")}
            placeholder={role === "prestataire" ? "Facultatif — sera dans votre profil" : ""}
            data-testid="register-raison"
          />
        </div>
        )}

        {role === "entreprise" && (
          <div>
            <label htmlFor="register-type-structure" className="label">Type de structure</label>
            <select
              id="register-type-structure"
              required
              className="select"
              value={form.type_structure}
              onChange={set("type_structure")}
              data-testid="register-type-structure"
            >
              <option value="">— Sélectionnez —</option>
              {TYPE_STRUCTURE_OPTIONS.map((o) => (
                <option key={o.v} value={o.v}>{o.t}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label htmlFor="register-email" className="label">E-mail</label>
          <input
            id="register-email"
            required
            type="email"
            autoComplete="email"
            inputMode="email"
            className="input"
            value={form.email}
            onChange={set("email")}
            data-testid="register-email"
          />
        </div>

        <div>
          <label htmlFor="register-password" className="label">Mot de passe</label>
          <div className="relative">
            <input
              id="register-password"
              required
              type={showPwd ? "text" : "password"}
              minLength={8}
              autoComplete="new-password"
              className="input pr-12"
              value={form.password}
              onChange={set("password")}
              data-testid="register-password"
            />
            <button
              type="button"
              onClick={() => setShowPwd((s) => !s)}
              className="absolute inset-y-0 right-0 px-3 flex items-center text-[#3E4C59] hover:text-[#1B3A5C]"
              aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              data-testid="register-password-toggle"
            >
              {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <p className="mt-1.5 text-xs text-[#3E4C59]">8 caractères minimum.</p>
        </div>

        <button
          disabled={busy}
          className="btn-primary w-full min-h-[56px] text-base"
          data-testid="register-submit"
        >
          {busy ? "Création…" : "Créer mon compte"}
          {!busy && <ArrowUpRight size={18} />}
        </button>
      </form>

      <p className="mt-6 text-sm text-[#3E4C59] text-center">
        Déjà un compte ?{" "}
        <Link
          to="/connexion"
          className="font-bold text-[#1B3A5C] underline hover:text-[#E8710A] transition-colors"
          data-testid="register-login-link"
        >
          Se connecter
        </Link>
      </p>
    </div>
  );
}

function SiretStatus({ status, onEdit }) {
  if (status.state === "idle") return null;
  if (status.state === "loading") {
    return (
      <p className="mt-2 flex items-center gap-2 text-xs text-[#3E4C59]" data-testid="siret-loading">
        <Loader2 size={14} className="animate-spin" /> Recherche entreprise en cours…
      </p>
    );
  }
  if (status.state === "found") {
    return (
      <div
        className="mt-2 flex items-start gap-2 text-xs text-[#1F2933] bg-[#F5F3EE] border border-[#E4E7EB] rounded-[3px] px-3 py-2"
        data-testid="siret-found"
      >
        <Check size={14} className="text-[#1B3A5C] shrink-0 mt-0.5" strokeWidth={2.5} />
        <span className="flex-1">
          <span className="font-bold" data-testid="siret-denomination">{status.data.denomination}</span>
          {status.data.code_postal && (
            <span className="text-[#3E4C59]"> — {status.data.code_postal} {status.data.ville}</span>
          )}
          {onEdit && (
            <button type="button" onClick={onEdit} className="block mt-1 underline text-[#3E4C59] hover:text-[#1B3A5C]" data-testid="siret-edit-raison">
              Ce n'est pas la bonne dénomination ? Modifier
            </button>
          )}
        </span>
      </div>
    );
  }
  return (
    <p className="mt-2 flex items-start gap-1.5 text-xs text-[#3E4C59]" data-testid="siret-not-found">
      <Info size={13} className="shrink-0 mt-0.5" />
      SIRET non trouvé dans la base publique — indiquez votre raison sociale ci-dessous.
    </p>
  );
}

/* --- Écran 3 : succès + rassurance ---------------------------------- */

function SuccessStep({ user, onContinue }) {
  return (
    <div>
      <div className="mb-8 md:mb-10 flex items-center gap-3">
        <div className="h-px w-8 bg-[#E8710A]" aria-hidden />
        <span className="text-[11px] uppercase tracking-[0.24em] font-bold text-[#E8710A] font-mono-l">
          Compte créé
        </span>
      </div>

      <div className="bg-white border border-[#1B3A5C]/10 rounded-[8px] p-6 md:p-10 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#E8710A] text-white flex items-center justify-center mb-6">
          <MailCheck size={26} strokeWidth={2} />
        </div>

        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-[#0F2338] tracking-[-0.02em] leading-[1.15]">
          Bienvenue.
        </h1>

        <p className="mt-4 text-base md:text-lg text-[#3E4C59] leading-relaxed">
          Un lien de confirmation a été envoyé à{" "}
          <span
            className="font-bold text-[#1F2933] break-all"
            data-testid="register-success-email"
          >
            {user?.email}
          </span>
          .
        </p>

        <p className="mt-3 text-sm md:text-base text-[#3E4C59] leading-relaxed">
          <span className="font-bold text-[#1F2933]">Ça peut attendre</span> — commencez déjà votre profil, on vous demandera de confirmer plus tard.
        </p>

        <button
          type="button"
          onClick={onContinue}
          className="btn-primary mt-8 w-full sm:w-auto min-h-[56px] px-8 text-base"
          data-testid="register-success-continue"
        >
          Continuer vers mon profil <ArrowUpRight size={18} />
        </button>
      </div>
    </div>
  );
}

/* --- Page ----------------------------------------------------------- */

export default function Register() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const initialRole = sp.get("role") === "prestataire" || sp.get("role") === "entreprise"
    ? sp.get("role") : null;

  const [step, setStep] = useState(initialRole ? "form" : "role");
  const [role, setRole] = useState(initialRole || "entreprise");
  const [createdUser, setCreatedUser] = useState(null);

  const pickRole = (r) => { setRole(r); setStep("form"); };
  const goBackToRole = () => setStep("role");
  const onSuccess = (u) => {
    setCreatedUser(u);
    setStep("success");
    toast.success("Compte créé");
  };
  const onContinue = () => {
    nav(createdUser?.role === "prestataire" ? "/app/mon-profil" : "/app/dashboard");
  };

  return (
    <AuthShell promise="Inscription">
      <Helmet>
        <title>Inscription — VosCompagnons</title>
        <meta name="description" content="Créez votre compte VosCompagnons — plateforme B2B du bâtiment. Réservé aux professionnels, SIRET obligatoire. Inscription et usage gratuits. Commission de 8 % uniquement sur les chantiers réalisés via la plateforme." />
        <link rel="canonical" href="https://voscompagnons.fr/inscription" />
        <meta property="og:title" content="Inscription — VosCompagnons" />
        <meta property="og:description" content="Réservé aux professionnels du bâtiment. Inscription et usage gratuits. Commission de 8 % uniquement sur les chantiers réalisés via la plateforme." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://voscompagnons.fr/inscription" />
        <meta property="og:locale" content="fr_FR" />
      </Helmet>
          {step === "role" && <RoleStep onPick={pickRole} />}
          {step === "form" && <FormStep role={role} onBack={goBackToRole} onSuccess={onSuccess} />}
          {step === "success" && <SuccessStep user={createdUser} onContinue={onContinue} />}
    </AuthShell>
  );
}
